import { type FC, useState, useEffect, useCallback } from 'react';
import { createRefetchContainer, graphql, type RelayRefetchProp } from 'react-relay/legacy';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import { TRADE } from 'dibs-buyer-layout/exports/userTypeEnums';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers';
import { trackPageview, trackUserEventHomepageView } from 'dibs-tracking';
import { mobile } from 'dibs-client-check';
import { useIntl } from 'dibs-react-intl';
import { useHomePageContext } from './HomePageContext';
import getLazyLoadStartingPoint from './helpers/getLazyLoadStartingPoint';
import { HpSharedAppBannerLazy } from './HpSharedAppBanner/HpSharedAppBannerLazy';
import { HpSharedCompleteEmailOptinConfirmationModalLazy } from './HpSharedCompleteEmailOptinConfirmationModal/HpSharedCompleteEmailOptinConfirmationModalLazy';
import {
    getUserSessionCountryCode,
    getUserZipCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { componentModuleMap } from './componentModuleMap';
import { isVideoComponentTestVariant } from './helpers/abTestVideoComponent';
import { isTrendingCreatorsVariant } from './helpers/abTestTrendingCreators';
import { HpSharedBillboardAd } from './HpSharedBillboardAd/HpSharedBillboardAd';
import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { HpSharedRecentlyViewedItems } from './HpSharedRecentlyViewedItems/HpSharedRecentlyViewedItems';
import HpSharedNewArrivals from './HpSharedNewArrivals/HpSharedNewArrivals';
import {
    HOME_BOTTOM_BILLBOARD_PLACEMENT,
    HOME_TOP_BILLBOARD_PLACEMENT,
} from 'dibs-google-ad-manager/exports/constants';
import { trackBannerAdsAbTestVariant } from 'dibs-google-ad-manager/exports/abTestHelpers';

import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';

const HpSharedVideoComponent = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "HpSharedVideoComponent"  */ './HpSharedVideoComponent/HpSharedVideoComponent'
        )
);

import { type HpSharedLayout_viewer$data } from './__generated__/HpSharedLayout_viewer.graphql';
import { trackContentPlacementsTest } from './helpers/abTestContentPlacements';
import HpSharedSkinnyBannerModule from './HpSharedSkinnyBannerModule/HpSharedSkinnyBannerModule';
import { HpSharedFeaturedModule } from './HpSharedFeaturedModule/HpSharedFeaturedModule';

type HpSharedLayoutProps = {
    relay: RelayRefetchProp;
    homepageUserGroup: string;
    viewer: HpSharedLayout_viewer$data;
};

const HpSharedLayoutComponent: FC<HpSharedLayoutProps> = ({ relay, viewer, homepageUserGroup }) => {
    const intl = useIntl();
    const [userId, setUserId] = useState<string | null>(null);
    const [userType, setUserType] = useState<string | null>(null);
    const [isClient, setIsClient] = useState<boolean>(false);
    const [fetchFavorites, setFetchFavorites] = useState(false);
    const [isVideoTestVariant, setIsVideoTestVariant] = useState(false);

    const { isMobile, isTablet, isLoggedInUser, isTopDisplayAdEnabled, isBottomDisplayAdEnabled } =
        useHomePageContext();

    const user = viewer?.user || null;

    const doRefetch = useCallback(
        (additionalVariables = {}) => {
            const _userType = homepageUserGroup || userType || '';

            relay.refetch(
                fragmentVariables => ({
                    ...fragmentVariables,
                    fetchInitialHomepage: false,
                    isClient: true,
                    isMobile,
                    hasUserId: !!userId,
                    userId: userId || '',
                    userIds: userId ? [userId] : [],
                    userType: _userType,
                    zipCode: getUserZipCode(),
                    isTrade: _userType === TRADE,
                    userCountryCode: getUserSessionCountryCode(),
                    isVideoComponentTestVariant: isVideoComponentTestVariant(),
                    isTrendingCreatorsVariant: isTrendingCreatorsVariant(),
                    ...additionalVariables,
                }),
                { fetchInitialHomepage: true },
                () => {
                    setIsClient(true);

                    // ensure that we are done with refetch before considering to allow favorites fetch
                    if (userId) {
                        setFetchFavorites(true);
                    }
                }
            );
        },
        [userId, userType, relay, isMobile, homepageUserGroup]
    );

    const setUserData = useCallback(() => {
        const cookieUserId = getBuyerId(document.cookie);

        if (cookieUserId && !userId) {
            // order is important to prevent unnecessary refetch, read more: https://github.com/1stdibs/ferrum/pull/13601#pullrequestreview-818832289
            setUserType(getUserType(document.cookie));
            setUserId(cookieUserId);
        }
    }, [userId]);

    useEffect(() => {
        setIsVideoTestVariant(isClient && isVideoComponentTestVariant());
    }, [isClient]);

    useEffect(() => {
        trackBannerAdsAbTestVariant();
    }, []);

    useEffect(() => {
        trackContentPlacementsTest();
    }, []);

    useEffect(() => {
        trackPageview({ additional: { opt_pageURL: '/' } });
    }, []);

    useEffect(() => {
        trackUserEventHomepageView(relay.environment);
    }, [relay.environment]);

    useEffect(() => {
        if (!isLoggedInUser && !isClient) {
            doRefetch();
        }
    }, [doRefetch, isLoggedInUser, isClient]);

    useEffect(() => {
        if (userId && !user) {
            doRefetch();
        }
    }, [doRefetch, userId, user]);

    useEffect(() => {
        setUserData();
        addLoginHandlers((data: { user: { isVerifiedTrade?: boolean | null } }) => {
            if (data?.user?.isVerifiedTrade) {
                // keep this to make sure a trade member
                // logging in on the hp is "redirected"
                // to the trade homepage
                window.location.reload();
                return;
            }
            setUserData();
        });
    }, [setUserData, intl.locale]);

    useDwelledOnPage(PAGE_TYPE.HOME);

    const { homepage } = viewer;

    const isUser = isLoggedInUser || !!userId; // isLoggedInUser comes at load; userId might come later from refetch

    const { personalization, modules } = homepage || {};

    const promoPrimaryModule = (modules || []).find(
        mod => mod && mod.type === 'promo' && mod.index === 0
    );
    const promoPrimaryItemsCount = (promoPrimaryModule?.messages?.items || []).length;
    const startLazyLoadAt = getLazyLoadStartingPoint({ isMobile, isTablet });
    const hasLoggedInWithApp = user?.hasLoggedInWithApp;

    const mods = (modules || []).map((mod, index) => {
        if (!mod) {
            return null;
        }
        const type = mod.type;

        if (!type) {
            return null;
        }

        const component = componentModuleMap[type];
        if (component) {
            return component({
                componentModule: mod,
                key: `${type}-${index}`,
                startingPromoIndex: type === 'promo' && mod.index ? promoPrimaryItemsCount : 0,
                viewer,
                isMobile,
                useLazyLoadImages: index >= startLazyLoadAt,
                moduleIndex: index,
                totalModules: modules?.length || 0,
                user,
                isClient,
            });
        }
        return null;
    });

    const getModuleIndex = ({ moduleKey }: { moduleKey: string }): number => {
        const moduleIndex = mods.findIndex(mod => (mod?.key || '').toString().includes(moduleKey));

        if (moduleIndex === -1) {
            return mods.length - 1;
        }

        return moduleIndex;
    };

    /**
     * non cms controlled modules insertion
     */
    let maxRecentlyViewedItems = 12;
    let recentlyViewedItemsPerPage = 5;
    if (isMobile) {
        recentlyViewedItemsPerPage = 2.5;
    } else if (isTablet) {
        recentlyViewedItemsPerPage = 5;
        maxRecentlyViewedItems = 10;
    }

    mods.splice(
        getModuleIndex({ moduleKey: 'topHeroBanner' }) + 1,
        0,
        <RecentlyViewedItemsProvider
            key="hp-recentlyViewed"
            itemsPerPage={recentlyViewedItemsPerPage}
            maxItems={maxRecentlyViewedItems}
        >
            <HpSharedRecentlyViewedItems
                isMobile={isMobile}
                moduleIndex={1}
                totalModules={modules?.length || 0}
            />
        </RecentlyViewedItemsProvider>
    );

    if (isUser) {
        mods.splice(
            getModuleIndex({ moduleKey: 'editorial' }) + 1,
            0,
            <HpSharedNewArrivals
                key="hp-new-arrivals"
                viewer={viewer}
                user={user || null}
                personalization={personalization || null}
                fetchFavorites={fetchFavorites}
            />
        );
    }

    mods.splice(
        getModuleIndex({ moduleKey: 'locations' }) + 1,
        0,
        <HpSharedFeaturedModule
            key="featured"
            viewer={isClient ? viewer : null}
            user={user}
            isClient={isClient}
        />
    );

    mods.splice(
        getModuleIndex({ moduleKey: 'shopBy' }),
        0,
        <HpSharedSkinnyBannerModule key="skinnyBanner" />
    );

    if (isVideoTestVariant) {
        mods.splice(
            getModuleIndex({ moduleKey: 'featured' }) + 1,
            0,
            <ClientSuspense key="videoComponent" fallback={null}>
                <HpSharedVideoComponent
                    viewer={viewer}
                    user={user}
                    fetchFavorites={fetchFavorites}
                />
            </ClientSuspense>
        );
    }

    if (isTopDisplayAdEnabled) {
        mods.splice(
            getModuleIndex({ moduleKey: 'topHeroBanner' }) + 1,
            0,
            <HpSharedBillboardAd
                key="billboardAd-home-top"
                placement={HOME_TOP_BILLBOARD_PLACEMENT}
            />
        );
    }

    if (isBottomDisplayAdEnabled) {
        mods.splice(
            getModuleIndex({ moduleKey: 'locations' }) + 1,
            0,
            <HpSharedBillboardAd
                key="billboardAd-home-bottom"
                placement={HOME_BOTTOM_BILLBOARD_PLACEMENT}
            />
        );
    }

    return (
        <>
            {mods}
            {isClient && <HpSharedCompleteEmailOptinConfirmationModalLazy />}
            {isClient &&
                (mobile.iPhone() || mobile.iPad()) &&
                !(isLoggedInUser && hasLoggedInWithApp) && <HpSharedAppBannerLazy />}
        </>
    );
};

export const HpSharedLayout = createRefetchContainer(
    HpSharedLayoutComponent,
    {
        viewer: graphql`
            fragment HpSharedLayout_viewer on Viewer {
                homepage(rootId: $homepageId, abTestList: $abTestList) {
                    modules {
                        # don't skip this module on refetch, otherwise the dibs-carousel within
                        # won't get any items
                        ... on TopHeroBannerModule {
                            type
                        }
                        ... on HeroModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpSharedHeroModule_componentModule
                        }
                        ... on CollectionsModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpSharedCollectionsModule_componentModule
                        }
                        ... on PromoModule {
                            index
                            type
                            messages(userType: $userType) {
                                items {
                                    __typename
                                }
                            }
                            ...HpSharedPromoModule_componentModule
                        }
                        ... on EditorialModule @include(if: $fetchInitialHomepage) {
                            type
                        }
                        ... on ShopByModule {
                            type
                            ...HpSharedShopByModule_componentModule
                        }
                        ... on LocationsModule {
                            type
                        }
                    }
                    personalization(userId: $userId, first: 3, numItems: 3)
                        @include(if: $hasUserId) {
                        ...HpSharedNewArrivals_personalization
                    }
                }
                ...HpSharedHeroModule_viewer
                ...HpSharedLocationsModule_viewer @include(if: $isClient)
                ...HpSharedNewArrivals_viewer
                ...HpSharedVideoComponent_viewer @include(if: $isVideoComponentTestVariant)
                ...HpSharedShopByModule_viewer
                ...HpSharedFeaturedModule_viewer @include(if: $isClient)
                user(userId: $userId) @include(if: $hasUserId) {
                    hasLoggedInWithApp: hasLoggedInWithDeviceType(deviceType: IOS_APP)
                    ...HpSharedHeroModule_user
                    ...HpSharedLocationsModule_user
                    ...HpSharedNewArrivals_user
                    ...HpSharedVideoComponent_user @include(if: $isVideoComponentTestVariant)
                    ...HpSharedFeaturedModule_user
                }
            }
        `,
    },
    graphql`
        query HpSharedLayoutRefetchQuery(
            $hasUserId: Boolean!
            $userId: String!
            $homepageId: String!
            $isMobile: Boolean!
            $fetchInitialHomepage: Boolean!
            $userType: String!
            $abTestList: [String] = []
            $userCountryCode: String!
            $isTrade: Boolean!
            $priceBookName: String!
            $isClient: Boolean!
            $isVideoComponentTestVariant: Boolean!
            $isTrendingCreatorsVariant: Boolean!
        ) {
            viewer {
                ...HpSharedLayout_viewer
            }
        }
    `
);
